import React, { useMemo } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider, useQueryClient } from 'react-query/react';
import { QueryClient } from 'react-query/core';
import { ReactQueryDevtools } from 'react-query/devtools';
import {
  makeReduxStore,
  TokenizedApi,
  TSdkOptions,
} from '@tokenized/sdk-js-private';
import { tokenizedApiContext } from '../internal/context';
import { tokenizedApiStoreContext } from '../internal/redux';

const DEVTOOLS_ENABLED = !!window.localStorage.getItem(
  'tokenized-sdk-devtools-enable',
);

const nullReduxStore = makeReduxStore(null);
const nullQueryClient = new QueryClient();

/**
 * `<TokenizedApiProvider>` is a React context provider component, for passing
 * the [TokenizedApi]{@link TokenizedApi}
 * session manager down to components that use queries and mutations. Use this
 * once at the top of your app’s component tree.
 * Either supply a previously created TokenizedApi, or the configuration
 * options to create a new one. Supplying a configuration will enable React Query
 * context sharing between the host application and the SDK.
 *
 * Sometimes it’s useful to turn off the SDK and clear the context, for example
 * if you need to get data asynchronously in order to construct the config. To
 * achieve this, pass `null` as the `tokenizedApi` prop.
 *
 * #### Devtools
 *
 * The SDK caches model data with [React Query](https://react-query.tanstack.com)
 * and stores state using [Redux](https://redux.js.org). To avoid confusion with
 * your app’s usage of these frameworks, devtools support is turned off by default.
 * If you’d like to view the SDK’s internal state in development builds, run this
 * code in your browser’s console, and reload:
 *
 *     window.localStorage.setItem('tokenized-sdk-devtools-enable', 'true');
 *
 * The React Query devtools are rendered inside the `<TokenizedApiProvider>`
 * component. In the Redux devtools browser extension, you should be able to
 * select `Tokenized SDK` from the list of stores. To turn the devtools off again, run:
 *
 *     window.localStorage.removeItem('tokenized-sdk-devtools-enable');
 *
 * #### Example
 *
 * ```js
 * import React from 'react';
 * import ReactDOM from 'react-dom';
 * import { TokenizedApi } from '@tokenized/sdk-js-private';
 * import App from './App';
 *
 * const config = {
 *   tokenizedBackend: 'development',
 *   applicationIdentifier: 'tokenized-example-react',
 * });
 *
 * ReactDOM.render(
 *   <TokenizedApiProvider tokenizedApi={tokenizedApi}>
 *     <App />
 *   </TokenizedApiProvider>,
 *   document.getElementById('root'),
 * );
 * ```
 *
 * @category React Components
 */
export function TokenizedApiProvider({
  children,
  config,
  tokenizedApi,
}: {
  children: React.ReactNode;
  /** Supply the session manager object you created on startup. Can be `null`. */
  tokenizedApi?: TokenizedApi | null;
  /**
   * Configuration options for a new [TokenizedApi]{@link TokenizedApi} instance
   * (ignored if you supply a value for `tokenizedApi`).
   */
  config?: TSdkOptions;
}) {
  let queryClient: QueryClient | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    queryClient = useQueryClient();
  } catch (e) {
    // No QueryClient found, one will be created (if not supplied)
    queryClient = undefined;
  }

  // Note the special-case handling here for tokenizedApi being
  // null, which is used in the mobile app in cases where the
  // tokenizedApi instance is obtained asynchronously (see
  // OptionalTokenizedApiProvider in the mobile code)
  const api = useMemo(
    () =>
      tokenizedApi !== undefined
        ? tokenizedApi
        : new TokenizedApi({ queryClient, ...config }),
    [tokenizedApi, config, queryClient],
  );

  // nullReduxStore and nullQueryClient are placeholders to
  // keep child components happy while tokenizedApi is null.
  let reduxStore = nullReduxStore;
  if (api) {
    reduxStore = api.getReduxStore();
    queryClient = api.getQueryClient();
  } else {
    queryClient = nullQueryClient;
  }

  // Used to turn on/off focus/network monitoring etc
  React.useEffect(() => {
    api?.mount?.();
    return () => {
      api?.unmount?.();
    };
  }, [api]);

  return (
    <tokenizedApiContext.Provider value={api}>
      <QueryClientProvider client={queryClient}>
        {DEVTOOLS_ENABLED && !!api && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        <ReduxProvider context={tokenizedApiStoreContext} store={reduxStore}>
          {children}
        </ReduxProvider>
      </QueryClientProvider>
    </tokenizedApiContext.Provider>
  );
}
